import {
    ChangeDetectionStrategy,
    Component,
    Inject,
    Injectable,
    ViewEncapsulation,
} from '@angular/core';
import { AppStateFacade } from '@app-state';
import {
    FOLDER_TREE_SOURCE,
    FolderTreeStateService,
} from '@components/ui-folder-tree/ui-folder-tree-state';
import {
    FolderType,
    IFolder,
    ModalResultType,
    ODataResponse,
} from '@models';
import { TranslateService } from '@ngx-translate/core';
import {
    FoldersService,
    ModalService,
    ODataQueryOptions,
} from '@services';

import omit from 'lodash-es/omit';
import {
    Observable,
    of,
} from 'rxjs';
import {
    filter,
    map,
    switchMap,
    tap,
} from 'rxjs/operators';

import { UpsertFolderModalComponent } from './modals/upsert-folder/upsert-folder.modal';

const MAX_FOLDER_DEPTH = 7;

@Injectable()
export class FolderManagementService extends FoldersService {
    public hasSwitchCapability = false;
    protected _endpoint = 'odata/Folders';

    public getFolders = (expandedIds: number[], take: number, skip: number) => {
        return this._http.get(
            this._endpoint +
            `/UiPath.Server.Configuration.OData.GetFoldersPage(skip=${skip},take=${take},expandedParentIds=[${expandedIds}])`,
        ).pipe(
            map(this._mapData),
        ) as Observable<ODataResponse<IFolder>>;
    }

    public searchFolders = (skip: number, take: number, searchText: string) => {
        if (!searchText) {
            return of({
                total: 0,
                data: [],
            });
        }

        const queryOptions: ODataQueryOptions = {
            skip: skip,
            top: take,
            filter: {
                'DisplayName': {
                    contains: searchText,
                },
            },
        };

        return this.get(queryOptions);
    }
}

@Component({
    templateUrl: './folders.page.html',
    styleUrls: ['./folders.page.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    providers: [
        FolderTreeStateService,
        {
            provide: FOLDER_TREE_SOURCE,
            useClass: FolderManagementService,
        },
    ],
})
export class FoldersPageComponent {
    public readonly MaxFolderDepth = MAX_FOLDER_DEPTH;
    public readonly FolderType = FolderType;
    public canViewUsersGrid$: Observable<boolean>;

    public currentHighlight$ = this.folderTreeState.highlighted$
        .pipe(
            filter(f => !!f),
            map(f => f!.id),
        );

    constructor(
        private _appState: AppStateFacade,
        public folderTreeState: FolderTreeStateService,
        @Inject(FOLDER_TREE_SOURCE)
        private _foldersService: FoldersService,
        private _modal: ModalService,
        private _translateService: TranslateService,
    ) {
        this.canViewUsersGrid$ = this.currentHighlight$.pipe(
            switchMap(folder => this._appState.user
                .folderPermissions.isGranted(folder, 'SubFolders.View & Users.View | Units.View & Users.View')),
        );
    }

    public openEditFolderModal(folder: IFolder) {
        this._modal
            .openModal(UpsertFolderModalComponent, {
                current: folder,
            })
            .afterClosedResult()
            .pipe(
                tap(result => {
                    if (result.type === ModalResultType.Failed) {
                        throw result.additionalData;
                    }
                }),
                filter(result =>
                    result.type === ModalResultType.Update &&
                    !!this.folderTreeState.highlighted$.value,
                ),
                switchMap(result => this._foldersService.getInstance(result.data!.current!.id)),
            )
            .subscribe(
                updatedFolder => {
                    this.folderTreeState.patchFolder(
                        omit(
                            updatedFolder,
                            ['isSelectable', 'level', 'hasChildren'],
                        ),
                    );
                },
                () => this._refreshFolders(),
            );
    }

    public openCreateFolderModal(parent?: IFolder) {
        this._modal
            .openModal(UpsertFolderModalComponent, {
                parent: parent || null,
            })
            .afterClosedResult()
            .subscribe(() => {
                if (parent && !this.folderTreeState.isExpanded(parent.id)) {
                    this.folderTreeState.toggleExpand(
                        parent,
                        this.folderTreeState.folderIndexById(parent.id),
                    );
                }
                this._refreshFolders();
            });
    }

    public removeFolder(folder: IFolder) {
        this._modal
            .onConfirm(
                null,
                () =>
                    this._foldersService.delete(folder.id)
                        .subscribe(() => {
                            this._refreshFolders();
                        }),
                () => { },
                this._translateService.instant('folders_page_are_you_sure_remove'),
            );

    }

    public folderPermission = (...types: ('Delete' | 'Edit' | 'Create')[]) =>
        types.map(t => `Units.${t}|SubFolders.${t}`).join('|')

    private _refreshFolders() {
        this.folderTreeState.refresh();
    }
}
